/* RESPONSIBLE TEAM: team-app-security */

import { inject as service } from '@ember/service';
import { setOwner } from '@ember/application';
import { warn } from '@ember/debug';
import formatTimezoneHelper from 'embercom/helpers/format-timezone';
import formatArrayToSentence from 'embercom/helpers/format-array-to-sentence';
import inflectorHelper from 'embercom/helpers/inflector-helper';
import {
  OUTBOUND_EDITOR_ROUTES,
  SELECTION_SET_ROUTES,
  WORKFLOW_ROUTES,
} from 'embercom/models/outbound/content-wrapper';
import { EMAIL_SETTING_TYPES } from './email-settings-constants';
import Metrics from 'embercom/models/metrics';

export default class AppAdminEventsHelper {
  @service intl;
  @service appService;
  @service router;
  @service store;
  @service reportAccessService;

  constructor(owner) {
    setOwner(this, owner);
  }

  get app() {
    return this.appService.app;
  }

  get inboxBaseUrl() {
    return this.router.urlFor(this.app.inboxBaseRoute, this.app);
  }

  adminUrl(adminId) {
    if (!adminId || this.app.canUseStandalone) {
      return '#';
    }
    return `/a/apps/${this.app.id}/admins/${adminId}`;
  }

  conversationUrl(conversationId) {
    if (!conversationId) {
      return '#';
    }
    return this.router.urlFor('apps.app.inbox.inbox.conversation', conversationId);
  }

  formatDate(dateString) {
    if (!dateString) {
      return '';
    }
    let date = new Date(dateString);
    let day = String(date.getUTCDate()).padStart(2, '0');
    let month = String(date.getUTCMonth() + 1).padStart(2, '0');
    let year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }

  // The names of content we get from Rails do not match exactly to the names of content we have
  // in the routes. Ideally we should return the entity enums and not the names from rails.
  // In the meantime, we can handle differing names by extending the list of editor routes here.
  get outboundRoutes() {
    return Object.assign({}, OUTBOUND_EDITOR_ROUTES, { sms_message: 'apps.app.outbound.sms.edit' });
  }

  outboundEditorUrl(event) {
    let contentType = event.value.content_type;
    if (contentType) {
      let route = this.outboundRoutes[contentType];

      if (route) {
        return this.router.urlFor(route, event.value.ruleset_id);
      } else {
        Metrics.capture({
          increment: ['app-admin-events-helper.outbound-editor-route-not-found'],
        });
        console.error(`No outbound editor route found for [${contentType}]`);
        return this.router.urlFor(`apps.app.outbound`);
      }
    }
  }

  outboundSelectionSetUrl(event) {
    return this.router.urlFor(SELECTION_SET_ROUTES.all, event.value.selection_set_id);
  }

  workflowNameOrUntitled(event) {
    if (event.value.ruleset_title && event.value.ruleset_title.trim().length > 0) {
      return event.value.ruleset_title;
    }
    return this.intl.t('table-cells.admin-events.workflow-untitled');
  }

  workflowStatusTranslation(status) {
    switch (status) {
      case 'live':
        return this.intl.t('table-cells.admin-events.description-cell.workflow.status.live');
      case 'paused':
        return this.intl.t('table-cells.admin-events.description-cell.workflow.status.paused');
      case 'unknown':
        return this.intl.t('table-cells.admin-events.description-cell.workflow.status.unknown');
      default:
        Metrics.capture({
          increment: ['app-admin-events-helper.workflow-status-unknown'],
        });
        console.error(`Unknown workflow status: ${status}. Falling back to unknown.`);
        return this.intl.t('table-cells.admin-events.description-cell.workflow.status.unknown');
    }
  }

  workflowUrl(event) {
    let content_type = event.value.content_type;
    let ruleset_id = event.value.ruleset_id;

    let route = WORKFLOW_ROUTES[content_type];

    if (route) {
      return this.router.urlFor(route, ruleset_id);
    } else {
      Metrics.capture({
        increment: ['app-admin-events-helper.workflow-route-not-found'],
      });
      console.error(`No workflow route found for [${content_type}]`);
      return this.router.urlFor('apps.app.automation.workflows-overview');
    }
  }

  rulesetContentTypeLabel(contentType) {
    if (!contentType) {
      contentType = 'message';
    }

    let normalizedContentType = contentType.replaceAll('-', '_');
    let translationKeyMap = {
      message: 'table-cells.admin-events.ruleset-content-type.message',
      email: 'table-cells.admin-events.ruleset-content-type.email',
      custom_bot: 'table-cells.admin-events.ruleset-content-type.custom-bot',
      chat: 'table-cells.admin-events.ruleset-content-type.chat',
      post: 'table-cells.admin-events.ruleset-content-type.post',
      broadcast: 'table-cells.admin-events.ruleset-content-type.broadcast',
      tour: 'table-cells.admin-events.ruleset-content-type.tour',
      push: 'table-cells.admin-events.ruleset-content-type.push',
      carousel: 'table-cells.admin-events.ruleset-content-type.carousel',
      banner: 'table-cells.admin-events.ruleset-content-type.banner',
      survey: 'table-cells.admin-events.ruleset-content-type.survey',
      sms: 'table-cells.admin-events.ruleset-content-type.sms',
      series: 'table-cells.admin-events.ruleset-content-type.series',
      bot: 'table-cells.admin-events.ruleset-content-type.bot',
      inbound_custom_bot: 'table-cells.admin-events.ruleset-content-type.inbound-custom-bot',
      button_custom_bot: 'table-cells.admin-events.ruleset-content-type.button-custom-bot',
      triggerable_custom_bot:
        'table-cells.admin-events.ruleset-content-type.triggerable-custom-bot',
      tooltip_group: 'table-cells.admin-events.ruleset-content-type.tooltip-group',
      news_item: 'table-cells.admin-events.ruleset-content-type.news-item',
      checklist: 'table-cells.admin-events.ruleset-content-type.checklist',
      whatsapp: 'table-cells.admin-events.ruleset-content-type.whatsapp',
    };

    let translationKey = translationKeyMap[normalizedContentType];

    if (!translationKey) {
      warn(
        `Unknown content type: "${normalizedContentType}". Falling back to default translation.`,
        false,
        {
          id: 'app-admin-events-helper-unknown-content-type',
        },
      );
      return this.intl.t('table-cells.admin-events.ruleset-content-type.default');
    }

    return this.intl.t(translationKey);
  }

  rulesetContentTypeArticle(contentTypeLabel) {
    if (!contentTypeLabel) {
      return 'a';
    }

    let firstChar = contentTypeLabel.charAt(0).toLowerCase();
    return ['a', 'e', 'i', 'o', 'u'].includes(firstChar) ? 'an' : 'a';
  }

  macroEditorUrl(macroId) {
    try {
      return `${this.router.urlFor('apps.app.settings.helpdesk.macros')}?selectedReplyId=${macroId}`;
    } catch (error) {
      console.error(`Error generating macro editor URL: ${error}`);
      return this.router.urlFor('apps.app.settings.helpdesk.macros');
    }
  }

  defaultEventParams(event) {
    let teammateName = `${event.value?.admin_first_name} ${event.value?.admin_last_name}`;
    if (event.admin_id === null && event.admin_ip === null) {
      return {
        htmlSafe: true,
        adminHref: 'https://www.intercom.com/',
        eventAdminName: 'Intercom',
        href1: this.inboxBaseUrl,
        appName: this.app.name,
        eventValueName: event.value?.name,
        teammateName,
      };
    }

    let adminHref = this.adminUrl(event.admin.id);
    return {
      htmlSafe: true,
      adminHref,
      eventAdminName: event.admin?.name || this.intl.t('table-cells.admin-events.unknown-admin'),
      href1: this.inboxBaseUrl,
      appName: this.app.name,
      eventValueName: event.value?.name,
      teammateName,
    };
  }

  reportAccessParams(event) {
    let entityName;

    if (event.value.access_admin_id) {
      entityName =
        event.value.entity_name ??
        this.intl.t('reporting.custom-reports.report.share-modal.former-teammate');
    } else if (event.value.access_role_id) {
      entityName =
        event.value.entity_name ??
        this.intl.t('reporting.custom-reports.report.share-modal.delete-role');
    } else {
      entityName = this.intl.t('reporting.custom-reports.report.share-modal.workspace-access');
    }

    return {
      entityName,
      reportId: event.value.report_id,
      reportHref: `/a/apps/${this.app.id}/reports/custom-reports/report/${event.value.report_id}`,
    };
  }

  descriptionHTML(event) {
    if (event.isAdminDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.removed-teammate', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isAdminProvisioned) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-provisioned', {
        ...this.defaultEventParams(event),
        href: this.adminUrl(event.value.admin_id),
      });
    } else if (event.isAdminDeprovisioned) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-deprovisioned', {
        ...this.defaultEventParams(event),
        href: this.adminUrl(event.value.admin_id),
        eventValueAdmin_name: event.value.admin_name,
      });
    } else if (event.isBillingAddresseesChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.billing-addressees-change',
        this.defaultEventParams(event),
      );
    } else if (event.isBillingAddresseesBounced) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.bounced-email-address',
        this.defaultEventParams(event),
      );
    } else if (event.isRoleDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.role-deletion', {
        ...this.defaultEventParams(event),
        href1: `/a/apps/${this.app.id}/settings/roles/${event.value.id}`,
        eventValueName: event.value.name,
      });
    } else if (event.isRoleCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.role-creation', {
        ...this.defaultEventParams(event),
        href1: `/a/apps/${this.app.id}/settings/roles/${event.value.id}`,
        eventValueName: event.value.name,
      });
    } else if (event.isRoleChange) {
      return this.intl.t('table-cells.admin-events.description-cell.role-change', {
        ...this.defaultEventParams(event),
        href1: `/a/apps/${this.app.id}/settings/roles/${event.value.static.role.id}`,
        eventValueAfterName: event.value.after.name,
      });
    } else if (event.isSeatAssigned && event.isSeatRevoked) {
      return this.intl.t('table-cells.admin-events.description-cell.seat-changed', {
        ...this.defaultEventParams(event),
        href1: this.adminUrl(event.value.admin_id),
        eventValueAdmin_first_name: event.value.admin_first_name,
        eventValueAdmin_last_name: event.value.admin_last_name,
      });
    } else if (event.isSeatAssigned) {
      return this.intl.t('table-cells.admin-events.description-cell.seat-assigned', {
        ...this.defaultEventParams(event),
        href1: this.adminUrl(event.value.admin_id),
        eventValueAdmin_first_name: event.value.admin_first_name,
        eventValueAdmin_last_name: event.value.admin_last_name,
        length: event.value.after.length - event.value.before.length,
        label: event.assignedSeatLabel,
      });
    } else if (event.isSeatRevoked) {
      return this.intl.t('table-cells.admin-events.description-cell.seat-revoked', {
        ...this.defaultEventParams(event),
        href1: this.adminUrl(event.value.admin_id),
        eventValueAdmin_first_name: event.value.admin_first_name,
        eventValueAdmin_last_name: event.value.admin_last_name,
        length: event.value.before.length - event.value.after.length,
        label: event.revokedSeatLabel,
      });
    } else if (event.isSeatChange) {
      return this.intl.t('table-cells.admin-events.description-cell.seat-revoked', {
        ...this.defaultEventParams(event),
        href1: this.adminUrl(event.value.admin_id),
        eventValueAdmin_first_name: event.value.admin_first_name,
        eventValueAdmin_last_name: event.value.admin_last_name,
      });
    } else if (event.isInboxAccessRevoked) {
      return this.intl.t('table-cells.admin-events.description-cell.inbox-access-revoked', {
        ...this.defaultEventParams(event),
        href1: this.adminUrl(event.value.admin_id),
        eventValueStaticAdminName: event.value.static.admin_name,
      });
    } else if (event.isInboxAccessGranted) {
      return this.intl.t('table-cells.admin-events.description-cell.inbox-access-granted', {
        ...this.defaultEventParams(event),
        href1: this.adminUrl(event.value.admin_id),
        eventValueStaticAdminName: event.value.static.admin_name,
      });
    } else if (event.isAdminInviteRoleChange) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-invite-role-change', {
        ...this.defaultEventParams(event),
        href1: `mailto:${event.value.static.email}`,
        eventValueStaticEmail: event.value.static.email,
        href2: `/a/apps/${this.app.id}/settings/roles/edit/${event.value.before.role.id}`,
        eventValueBeforeRoleName: event.value.before.role.name,
        href3: `/a/apps/${this.app.id}/settings/roles/edit/${event.value.after.role.id}`,
        eventValueAfterRoleName: event.value.after.role.name,
      });
    } else if (event.isAdminInviteRoleAssign) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-invite-role-assign', {
        ...this.defaultEventParams(event),
        href1: `/a/apps/${this.app.id}/settings/roles/edit/${event.value.after.role.id}`,
        eventValueAfterRoleName: event.value.after.role.name,
        href2: `mailto:${event.value.static.email}`,
        eventValueStaticEmail: event.value.static.email,
      });
    } else if (event.isAdminInviteRoleRemove) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-invite-role-remove', {
        ...this.defaultEventParams(event),
        href1: `/a/apps/${this.app.id}/settings/roles/edit/${event.value.before.role.id}`,
        eventValueBeforeRoleName: event.value.before.role.name,
        href2: `mailto:${event.value.static.email}`,
        eventValueStaticEmail: event.value.static.email,
      });
    } else if (event.isAdminInviteChange) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-invite-change', {
        ...this.defaultEventParams(event),
        href1: `mailto:${event.value.static.email}`,
        eventValueStaticEmail: event.value.static.email,
      });
    } else if (event.isAdminInviteCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-invite-creation', {
        ...this.defaultEventParams(event),
        href1: `mailto:${event.value.email}`,
        eventValueEmail: event.value.email,
      });
    } else if (event.isAdminBulkInviteCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-bulk-invite-creation', {
        ...this.defaultEventParams(event),
        href1: `mailto:${event.value.email}`,
        eventValueEmail: event.value.email,
      });
    } else if (event.isAdminInviteDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-invite-deletion', {
        ...this.defaultEventParams(event),
        href1: `mailto:${event.value.email}`,
        eventValueEmail: event.value.email,
      });
    } else if (event.isAdminRoleChange) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-role-change', {
        ...this.defaultEventParams(event),
        href1: this.adminUrl(event.value.static.admin_id),
        eventValueStaticAdmin_first_name: event.value.static.admin_first_name,
        eventValueStaticAdmin_last_name: event.value.static.admin_last_name,
        href2: `/a/apps/${this.app.id}/settings/roles/edit/${event.value.before.role.id}`,
        eventValueBeforeRoleName: event.value.before.role.name,
        href3: `/a/apps/${this.app.id}/settings/roles/edit/${event.value.after.role.id}`,
        eventValueAfterRoleName: event.value.after.role.name,
      });
    } else if (event.isAdminRoleAssign) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-role-assign', {
        ...this.defaultEventParams(event),
        href1: `/a/apps/${this.app.id}/settings/roles/edit/${event.value.after.role.id}`,
        eventValueAfterRoleName: event.value.after.role.name,
        href2: this.adminUrl(event.value.static.admin_id),
        eventValueStaticAdmin_first_name: event.value.static.admin_first_name,
        eventValueStaticAdmin_last_name: event.value.static.admin_last_name,
      });
    } else if (event.isAdminRoleRemove) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-role-remove', {
        ...this.defaultEventParams(event),
        href1: `/a/apps/${this.app.id}/settings/roles/edit/${event.value.before.role.id}`,
        eventValueBeforeRoleName: event.value.before.role.name,
        href2: this.adminUrl(event.value.static.admin_id),
        eventValueStaticAdmin_first_name: event.value.static.admin_first_name,
        eventValueStaticAdmin_last_name: event.value.static.admin_last_name,
      });
    } else if (event.isAdminPermissionChange) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-permission-change', {
        ...this.defaultEventParams(event),
        href1: this.adminUrl(event.value.static.admin_id),
        eventValueStaticAdmin_first_name: event.value.static.admin_first_name,
        eventValueStaticAdmin_last_name: event.value.static.admin_last_name,
      });
    } else if (event.isAdminAwayModeChange) {
      if (event.value.update_by && event.admin_id !== event.value.update_by) {
        return this.intl.t(
          'table-cells.admin-events.description-cell.admin-away-mode-change-by-teammate',
          {
            ...this.defaultEventParams(event),
            updateByAdminHref: this.adminUrl(event.value.update_by),
            updateByAdmin: event.value.update_by_name,
            eventValueAway_mode: event.value.away_mode,
            eventValueReassign_conversations: event.value.reassign_conversations,
          },
        );
      }

      return this.intl.t('table-cells.admin-events.description-cell.admin-away-mode-change', {
        ...this.defaultEventParams(event),
        eventValueAway_mode: event.value.away_mode,
        eventValueReassign_conversations: event.value.reassign_conversations,
      });
    } else if (event.isAdminAwayModeAutoChange) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-away-mode-auto-change', {
        ...this.defaultEventParams(event),
        eventValueAway_mode: event.value.away_mode,
        eventValueReassign_conversations: event.value.reassign_conversations,
      });
    } else if (event.isAppNameChange) {
      return this.intl.t('table-cells.admin-events.description-cell.app-name-change', {
        ...this.defaultEventParams(event),
        eventValueBefore: event.value.before,
        eventValueAfter: event.value.after,
      });
    } else if (event.isAppTimezoneChange) {
      return this.intl.t('table-cells.admin-events.description-cell.app-timezone-change', {
        ...this.defaultEventParams(event),
        eventValueBefore: formatTimezoneHelper.compute([event.value.before]),
        eventValueAfter: formatTimezoneHelper.compute([event.value.after]),
      });
    } else if (event.isBulkDataExportCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.bulk-data-export-creation', {
        ...this.defaultEventParams(event),
        eventValueBulkDataExport_bulk: event.value.bulk,
        eventValueBulkDataExport_schedule: event.value.schedule,
      });
    } else if (event.isBulkDataExportRun) {
      return this.intl.t('table-cells.admin-events.description-cell.bulk-data-export-run', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isBulkDelete) {
      return this.intl.t('table-cells.admin-events.description-cell.bulk-delete', {
        ...this.defaultEventParams(event),
        eventValueType: inflectorHelper.compute([event.value.type, event.value.count], {}),
        eventValueCount: event.value.count,
      });
    } else if (event.isBulkExport) {
      return this.intl.t('table-cells.admin-events.description-cell.bulk-export', {
        ...this.defaultEventParams(event),
        eventValueType: inflectorHelper.compute([event.value.type, event.value.count], {}),
        eventValueCount: event.value.count,
        columnSelection: event.value.with_all_attributes ? 'all' : 'some',
      });
    } else if (event.isMessageDeletion) {
      if (event.value.message_title) {
        return this.intl.t(
          'table-cells.admin-events.description-cell.message-deletion-with-title',
          {
            ...this.defaultEventParams(event),
            eventValueMessageTitle: event.value.message_title,
          },
        );
      } else {
        return this.intl.t('table-cells.admin-events.description-cell.message-deletion', {
          ...this.defaultEventParams(event),
        });
      }
    } else if (event.isRulesetActivation || event.isRulesetCreation || event.isRulesetDeletion) {
      let contentType = event.value.content_type ? event.value.content_type : 'message';
      let contentTypeLabel = this.rulesetContentTypeLabel(contentType);
      // Determine article based on translated label (check if it starts with a vowel)
      let article = this.rulesetContentTypeArticle(contentTypeLabel);
      let params = {
        ...this.defaultEventParams(event),
        rulesetHref: this.outboundEditorUrl(event),
        eventValueRuleset_id: event.value.ruleset_id,
        eventValueContent_type: contentTypeLabel,
        eventValueContent_type_article: article,
        eventValueContent_preview: event.value.content_preview
          ? event.value.content_preview
          : 'no_preview',
      };
      let eventType = event.type.toLowerCase().split('::ruleset')[1];
      if (event.value.ruleset_title) {
        return this.intl.t(`table-cells.admin-events.description-cell.ruleset-${eventType}-title`, {
          ...params,
          eventValueRuleset_title: event.value.ruleset_title,
        });
      } else {
        return this.intl.t(`table-cells.admin-events.description-cell.ruleset-${eventType}`, {
          ...params,
        });
      }
    } else if (event.isWorkflowCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.workflow.creation', {
        ...this.defaultEventParams(event),
        eventValueWorkflow_id: event.value.ruleset_id,
        eventValueWorkflow_name: this.workflowNameOrUntitled(event),
        workflowHref: this.workflowUrl(event),
      });
    } else if (event.isWorkflowDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.workflow.deletion', {
        ...this.defaultEventParams(event),
        eventValueWorkflow_id: event.value.ruleset_id,
        eventValueWorkflow_name: this.workflowNameOrUntitled(event),
      });
    } else if (event.isWorkflowUpdate) {
      return this.intl.t('table-cells.admin-events.description-cell.workflow.update', {
        ...this.defaultEventParams(event),
        eventValueWorkflow_id: event.value.ruleset_id,
        eventValueWorkflow_name: this.workflowNameOrUntitled(event),
        workflowHref: this.workflowUrl(event),
      });
    } else if (event.isWorkflowStatusChange) {
      return this.intl.t('table-cells.admin-events.description-cell.workflow.status-change', {
        ...this.defaultEventParams(event),
        eventValueWorkflow_id: event.value.ruleset_id,
        eventValueWorkflow_name: this.workflowNameOrUntitled(event),
        eventValueStatus: this.workflowStatusTranslation(event.value.status),
        workflowHref: this.workflowUrl(event),
      });
    } else if (event.isAdminLoginSuccess) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.admin-login-success',
        this.defaultEventParams(event),
      );
    } else if (event.isAdminLoginFailure) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-login-failure', {
        ...this.defaultEventParams(event),
        eventValueTimes: event.value.times,
      });
    } else if (event.isAdminLoginAttemptsReset) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-login-attempts-reset', {
        ...this.defaultEventParams(event),
        href1: this.adminUrl(event.value.target_admin_id),
        eventValueAdmin_name: event.value.target_admin_name,
      });
    } else if (event.isAdminTwoFactorLoginFailure) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.admin-two-factor-login-failure',
        {
          ...this.defaultEventParams(event),
          eventValueFailure_type: event.value.failure_type,
        },
      );
    } else if (event.isAdminLogout) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.admin-logout',
        this.defaultEventParams(event),
      );
    } else if (event.isArticleCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.article-creation', {
        ...this.defaultEventParams(event),
        articleHref: this.router.urlFor(
          'apps.app.articles.articles.article.show',
          event.value.article_id,
        ),
        eventValueArticle_locale: event.value.locale,
      });
    } else if (event.isArticleDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.article-deletion', {
        ...this.defaultEventParams(event),
        eventValueArticle_title: event.value.title,
        eventValueArticle_locale: event.value.locale,
      });
    } else if (event.isArticleContentCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.article-content-creation', {
        ...this.defaultEventParams(event),
        articleHref: this.router.urlFor(
          'apps.app.articles.articles.article.show',
          event.value.article_id,
        ),
        eventValueArticle_locale: event.value.locale,
      });
    } else if (event.isArticleContentUpdate) {
      return this.intl.t('table-cells.admin-events.description-cell.article-content-update', {
        ...this.defaultEventParams(event),
        articleHref: this.router.urlFor(
          'apps.app.articles.articles.article.show',
          event.value.article_id,
        ),
        eventValueArticle_locale: event.value.locale,
        eventValueArticle_title: event.value.title,
      });
    } else if (event.isArticleContentPublished) {
      return this.intl.t('table-cells.admin-events.description-cell.article-content-published', {
        ...this.defaultEventParams(event),
        articleHref: this.router.urlFor(
          'apps.app.articles.articles.article.show',
          event.value.article_id,
        ),
        eventValueArticle_locale: event.value.locale,
        eventValueArticle_title: event.value.title,
      });
    } else if (event.isArticleContentUnpublished) {
      return this.intl.t('table-cells.admin-events.description-cell.article-content-unpublished', {
        ...this.defaultEventParams(event),
        articleHref: this.router.urlFor(
          'apps.app.articles.articles.article.show',
          event.value.article_id,
        ),
        eventValueArticle_locale: event.value.locale,
        eventValueArticle_title: event.value.title,
      });
    } else if (event.isArticleContentDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.article-content-deletion', {
        ...this.defaultEventParams(event),
        eventValueArticle_locale: event.value.locale,
        eventValueArticle_title: event.value.title,
      });
    } else if (event.isMessageStateChange) {
      return this.intl.t('table-cells.admin-events.description-cell.message-state-change', {
        ...this.defaultEventParams(event),
        href1: this.router.urlFor('apps.app.outbound.message', event.value.static.message_id),
        eventValueBefore: event.value.before,
        eventValueAfter: event.value.after,
        eventValueStaticMessage_title: event.value.static.message_title
          ? event.value.static.message_title
          : 'a',
      });
    } else if (event.isCampaignDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.campaign-deletion', {
        ...this.defaultEventParams(event),
        eventValueCampaign_title: event.value.campaign_title ? event.value.campaign_title : 'a',
      });
    } else if (event.isCampaignStateChange) {
      return this.intl.t('table-cells.admin-events.description-cell.campaign-state-change', {
        ...this.defaultEventParams(event),
        eventValueBefore: event.value.before,
        eventValueAfter: event.value.after,
        eventValueCampaign_title: event.value.campaign_title ? event.value.campaign_title : 'a',
      });
    } else if (event.isAppAdminJoin) {
      return this.intl.t('table-cells.admin-events.description-cell.app-admin-join', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isAppAuthenticationMethodChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.app-authentication-method-change',
        {
          ...this.defaultEventParams(event),
          eventValueBefore: formatArrayToSentence.compute([event.value.before]),
          eventValueAfter: formatArrayToSentence.compute([event.value.after]),
        },
      );
    } else if (event.isAppDataDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.app-data-deletion', {
        ...this.defaultEventParams(event),
        eventValueType: event.value.type,
      });
    } else if (event.isAppIdentityVerificationChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.app-identity-verification-change',
        {
          ...this.defaultEventParams(event),
          eventValueIdentity_verification: event.value.identity_verification ? 'on' : 'off',
          eventValuePlatform: event.value.platform,
        },
      );
    } else if (event.isAppOutboundAddressChange) {
      return this.intl.t('table-cells.admin-events.description-cell.app-outbound-address-change', {
        ...this.defaultEventParams(event),
        eventValueBefore: event.value.before,
        eventValueAfter: event.value.after,
      });
    } else if (event.isAppPackageTokenRegeneration) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.app-package-token-regeneration',
        {
          ...this.defaultEventParams(event),
          eventValueApp_package_name: event.value.app_package_name,
        },
      );
    } else if (event.isAppWebhookCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.app-webhook-creation', {
        ...this.defaultEventParams(event),
        eventValueWebhook_url: event.value.webhook_url,
        eventValueWebhook_topics: formatArrayToSentence.compute([event.value.webhook_topics]),
      });
    } else if (event.isAppWebhookDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.app-webhook-deletion', {
        ...this.defaultEventParams(event),
        eventValueWebhook_url: event.value.webhook_url,
        eventValueWebhook_topics: formatArrayToSentence.compute([event.value.webhook_topics]),
      });
    } else if (event.isAppGoogleSsoDomainChange) {
      return this.intl.t('table-cells.admin-events.description-cell.app-google-sso-domain-change', {
        ...this.defaultEventParams(event),
        eventValueBefore: formatArrayToSentence.compute([event.value.before]),
        eventValueAfter: formatArrayToSentence.compute([event.value.after]),
      });
    } else if (event.isAppTeamCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.app-team-inbox-creation', {
        ...this.defaultEventParams(event),
        eventValueTeam_name: event.value.team_name,
        eventValueTeam_member_count: event.value.team_member_count,
      });
    } else if (event.isAppTeamDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.app-team-inbox-deletion', {
        ...this.defaultEventParams(event),
        eventValueTeam_name: event.value.team_name,
        eventValueTeam_member_count: event.value.team_member_count,
      });
    } else if (event.isAppTeamMembershipModification) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.app-team-inbox-membership-modification',
        {
          ...this.defaultEventParams(event),
          eventValueTeam_name: event.value.team_name,
        },
      );
    } else if (event.isAppPackageInstallation) {
      return this.intl.t('table-cells.admin-events.description-cell.app-package-installation', {
        ...this.defaultEventParams(event),
        eventValueApp_package_name: event.value.app_package_name,
      });
    } else if (event.isAppPackageUninstallation) {
      return this.intl.t('table-cells.admin-events.description-cell.app-package-uninstallation', {
        ...this.defaultEventParams(event),
        eventValueApp_package_name: event.value.app_package_name,
      });
    } else if (event.isAppDataExport) {
      return this.intl.t('table-cells.admin-events.description-cell.app-data-export', {
        ...this.defaultEventParams(event),
        eventValueType: inflectorHelper.compute([event.value.type, event.value.count], {}),
        eventValueCount: event.value.count,
      });
    } else if (event.isHelpCenterSettingsChange) {
      return this.intl.t('table-cells.admin-events.description-cell.help-center-settings-change', {
        ...this.defaultEventParams(event),
        eventValueTurned_on: event.value.turned_on ? 'on' : 'off',
      });
    } else if (event.isInboundConversationsChange) {
      return this.intl.t('table-cells.admin-events.description-cell.inbound-conversations-change', {
        ...this.defaultEventParams(event),
        eventValueTurned_on: event.value.turned_on ? 'on' : 'off',
        eventValueAudience: event.value.audience,
        eventValueSetting_name: event.value.setting_name
          ? event.value.setting_name
          : 'inboundConversations',
      });
    } else if (event.isMessengerSpacesChange) {
      return this.intl.t('table-cells.admin-events.description-cell.messenger-spaces-change', {
        ...this.defaultEventParams(event),
        eventValueAudience: event.value.audience,
      });
    } else if (event.isWelcomeMessageChange) {
      return this.intl.t('table-cells.admin-events.description-cell.welcome-message-change', {
        ...this.defaultEventParams(event),
        eventValueSetting_name: event.value?.setting_name
          ? event.value.setting_name
          : 'welcomeMessage',
      });
    } else if (event.isStartConversationButtonTextChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.start-conversation-button-text-change',
        {
          ...this.defaultEventParams(event),
          eventValueSetting_name: event.value?.setting_name,
          eventValueBefore: event.value?.before_value,
          eventValueAfter: event.value?.after_value,
        },
      );
    } else if (event.isLookAndFeelChange) {
      return this.intl.t('table-cells.admin-events.description-cell.look-and-feel-change', {
        ...this.defaultEventParams(event),
        eventValueSetting_name: event.value.setting_name,
      });
    } else if (event.isBrandSettingsChange) {
      return this.intl.t('table-cells.admin-events.description-cell.brand-settings-change', {
        ...this.defaultEventParams(event),
        eventValueSetting_name: event.value.setting_name,
      });
    } else if (event.isSearchBrowseEnabledChange) {
      return this.intl.t('table-cells.admin-events.description-cell.search-browse-enabled-change', {
        ...this.defaultEventParams(event),
        eventValueUser_type: event.value.user_type,
      });
    } else if (event.isUpfrontEmailCollectionChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.upfront-email-collection-change',
        {
          ...this.defaultEventParams(event),
          eventValueSetting_value: event.value.setting_value,
        },
      );
    } else if (event.isSearchBrowseRequiredChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.search-browse-required-change',
        {
          ...this.defaultEventParams(event),
          eventValueUser_type: event.value.user_type,
        },
      );
    } else if (event.isTemporaryExpectationChange) {
      return this.intl.t('table-cells.admin-events.description-cell.temporary-expectation-change', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isTemporaryExpectationMessagesChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.temporary-expectation-messages-change',
        {
          ...this.defaultEventParams(event),
          eventValueLocales: event.value.locales
            .map((locale) => this.intl.languageNameFromCode(locale))
            .join(', '),
        },
      );
    } else if (event.isSecuritySettingChange) {
      return this.intl.t('table-cells.admin-events.description-cell.security-setting-change', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isMaliciousDomainsSettingChange) {
      let action = event.value.enabled ? 'enabled' : 'disabled';
      return this.intl.t(
        'table-cells.admin-events.description-cell.malicious-domains-setting-change',
        {
          ...this.defaultEventParams(event),
          action,
        },
      );
    } else if (event.isTrustedDomainsSettingChange) {
      let action = event.value.enabled ? 'enabled' : 'disabled';
      return this.intl.t(
        'table-cells.admin-events.description-cell.trusted-domains-setting-change',
        {
          ...this.defaultEventParams(event),
          action,
        },
      );
    } else if (event.isOfficeHoursChange) {
      return this.intl.t('table-cells.admin-events.description-cell.office-hours-change', {
        ...this.defaultEventParams(event),
        eventValueSchedule_name: event.value.schedule_name,
        isDefault: event.value.is_default,
      });
    } else if (event.isPrivacyPolicyNoticeChange) {
      if (event.value.enabled) {
        return this.intl.t(
          'table-cells.admin-events.description-cell.privacy-policy-notice-enabled',
          {
            ...this.defaultEventParams(event),
          },
        );
      } else {
        return this.intl.t(
          'table-cells.admin-events.description-cell.privacy-policy-notice-disabled',
          {
            ...this.defaultEventParams(event),
          },
        );
      }
    } else if (event.isPrivacyPolicyNoticeContentsChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.privacy-policy-notice-contents-change',
        {
          ...this.defaultEventParams(event),
          eventValueLocales: event.value.locales
            .map((locale) => this.intl.languageNameFromCode(locale))
            .join(', '),
        },
      );
    } else if (event.isTopicCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.topic-creation', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isTopicDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.topic-deletion', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isTopicChange) {
      return this.intl.t('table-cells.admin-events.description-cell.topic-change', {
        ...this.defaultEventParams(event),
        eventValueBeforeName: event.value.before.name,
        eventValueAfterName: event.value.after.name,
        eq: event.value.before.name === event.value.after.name,
      });
    } else if (event.isTopicCurationSetLive) {
      return this.intl.t('table-cells.admin-events.description-cell.topic-curation-set-live', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isConversationPartDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.part-deletion', {
        ...this.defaultEventParams(event),
        partHref: this.router
          .urlFor('apps.app.inbox.inbox.conversation', event.value.conversation_id)
          .concat('#part_id=', event.value.part_identifier),
        eventConversationId: event.value.conversation_id,
        eventPartId: event.value.part_identifier,
      });
    } else if (event.isConversationPartRedaction) {
      return this.intl.t('table-cells.admin-events.description-cell.part-redaction', {
        ...this.defaultEventParams(event),
        partHref: this.router
          .urlFor('apps.app.inbox.inbox.conversation', event.value.conversation_id)
          .concat('#part_id=', event.value.part_identifier),
        eventConversationId: event.value.conversation_id,
        eventPartId: event.value.part_identifier,
      });
    } else if (event.isTwoFactorAuthChange) {
      return this.intl.t('table-cells.admin-events.description-cell.two-factor-auth-change', {
        ...this.defaultEventParams(event),
        eventValueState: event.value.state,
      });
    } else if (event.isAdminImpersonationStart) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-impersonation-start', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isAdminImpersonationEnd) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-impersonation-end', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isAdminImpersonationConsentApproved) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.admin-impersonation-consent-approved',
        {
          ...this.defaultEventParams(event),
        },
      );
    } else if (event.isAdminImpersonationConsentRevoked) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.admin-impersonation-consent-revoked',
        {
          ...this.defaultEventParams(event),
        },
      );
    } else if (event.isOauthTokenRevocation) {
      return this.intl.t('table-cells.admin-events.description-cell.oauth-token-revocation', {
        ...this.defaultEventParams(event),
        eventValueApp_package_name: event.value.app_package_name,
      });
    } else if (event.isEmailSignatureChange) {
      return this.intl.t('table-cells.admin-events.description-cell.email-signature-change', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isRecoveryCodeSent) {
      return this.intl.t('table-cells.admin-events.description-cell.recovery-code-sent', {
        ...this.defaultEventParams(event),
        eventTargetEmail: event.value.target_email_address,
      });
    } else if (event.isAdminUnauthorizedSignInMethod) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.admin-unauthorized-sign-in-method',
        {
          ...this.defaultEventParams(event),
          loginMethods: formatArrayToSentence.compute([event.value.current_sign_in_methods]),
          allowedLoginMethods: event.value.authorized_sign_in_methods,
        },
      );
    } else if (event.isAdminPasswordResetRequest) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-password-reset-request', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isAdminPasswordResetSuccess) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-password-reset-success', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isAdminAssignmentLimitChange) {
      let assignmentLimit = event.value.assignment_limit;

      if (!assignmentLimit) {
        return this.intl.t(
          'table-cells.admin-events.description-cell.admin-assignment-limit-unset',
          {
            ...this.defaultEventParams(event),
            adminNames: event.value.admins
              .map((adminId) => this.app.admins.findBy('id', String(adminId))?.name)
              .join(', '),
          },
        );
      } else {
        return this.intl.t(
          'table-cells.admin-events.description-cell.admin-assignment-limit-change',
          {
            ...this.defaultEventParams(event),
            assignmentLimit,
            adminNames: event.value.admins
              .map((adminId) => this.app.admins.findBy('id', String(adminId))?.name)
              .join(', '),
          },
        );
      }
    } else if (event.isAdminConversationAssignmentLimitChange) {
      let assignmentLimit = event.value.conversation_assignment_limit;

      if (!assignmentLimit) {
        return this.intl.t(
          'table-cells.admin-events.description-cell.admin-conversation-assignment-limit-unset',
          {
            ...this.defaultEventParams(event),
            adminNames: event.value.admins
              .map((adminId) => this.app.admins.findBy('id', String(adminId))?.name)
              .join(', '),
          },
        );
      } else {
        return this.intl.t(
          'table-cells.admin-events.description-cell.admin-conversation-assignment-limit-change',
          {
            ...this.defaultEventParams(event),
            assignmentLimit,
            adminNames: event.value.admins
              .map((adminId) => this.app.admins.findBy('id', String(adminId))?.name)
              .join(', '),
          },
        );
      }
    } else if (event.isAdminTicketAssignmentLimitChange) {
      let assignmentLimit = event.value.ticket_assignment_limit;

      if (!assignmentLimit) {
        return this.intl.t(
          'table-cells.admin-events.description-cell.admin-ticket-assignment-limit-unset',
          {
            ...this.defaultEventParams(event),
            adminNames: event.value.admins
              .map((adminId) => this.app.admins.findBy('id', String(adminId))?.name)
              .join(', '),
          },
        );
      } else {
        return this.intl.t(
          'table-cells.admin-events.description-cell.admin-ticket-assignment-limit-change',
          {
            ...this.defaultEventParams(event),
            assignmentLimit,
            adminNames: event.value.admins
              .map((adminId) => this.app.admins.findBy('id', String(adminId))?.name)
              .join(', '),
          },
        );
      }
    } else if (event.isTeamAssignmentLimitChange) {
      let assignmentLimit = event.value.team_assignment_limit;
      let teamName = this.app.teams.findBy('id', String(event.value.team_id))?.name;

      if (!assignmentLimit) {
        return this.intl.t(
          'table-cells.admin-events.description-cell.team-assignment-limit-unset',
          {
            ...this.defaultEventParams(event),
            teamName,
          },
        );
      } else {
        return this.intl.t(
          'table-cells.admin-events.description-cell.team-assignment-limit-change',
          {
            ...this.defaultEventParams(event),
            assignmentLimit,
            teamName,
          },
        );
      }
    } else if (event.isFinOptIn) {
      return this.intl.t('table-cells.admin-events.description-cell.fin-opt-in', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isIdvReminderDisabled) {
      return this.intl.t('table-cells.admin-events.description-cell.idv-reminder-disabled', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isMessengerPlatformAvailabilityChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.messenger-platform-availability-change',
        {
          ...this.defaultEventParams(event),
          eventStateChange: event.value.state,
          eventPlatform: event.value.platform,
        },
      );
    } else if (event.isMessengerPlatformInactivityDisableEvent) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.messenger-platform-inactivity-disable-event',
        {
          ...this.defaultEventParams(event),
          eventPlatform: event.value.platform,
        },
      );
    } else if (event.isIpAllowlistSettingChange) {
      return this.intl.t('table-cells.admin-events.description-cell.ip-allowlist-setting-change', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isAdminMobileLoginSuccess) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-mobile-login-success', {
        ...this.defaultEventParams(event),
        mobileAppName: event.value.mobile_app_name,
      });
    } else if (event.isAdminMobileLoginFailure) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-mobile-login-failure', {
        ...this.defaultEventParams(event),
        mobileAppName: event.value.mobile_app_name,
      });
    } else if (event.isAdminNameChange) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-name-change', {
        ...this.defaultEventParams(event),
        before: event.value.before,
        after: event.value.after,
      });
    } else if (event.isAdminEmailChange) {
      let source = event.value?.static?.source;
      if (source === 'scim') {
        return this.intl.t('table-cells.admin-events.description-cell.admin-email-change-by-scim', {
          ...this.defaultEventParams(event),
          before: event.value.before,
          after: event.value.after,
        });
      }
      return this.intl.t('table-cells.admin-events.description-cell.admin-email-change', {
        ...this.defaultEventParams(event),
        before: event.value.before,
        after: event.value.after,
      });
    } else if (event.isAdminPasswordChange) {
      return this.intl.t('table-cells.admin-events.description-cell.admin-password-change', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isReportCreatedEvent) {
      return this.intl.t('table-cells.admin-events.description-cell.report-create', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isReportUpdateEvent) {
      return this.intl.t('table-cells.admin-events.description-cell.report-update', {
        ...this.defaultEventParams(event),
        title: event.value.before.title || this.intl.t('reporting.custom-reports.report.untitled'),
        reportHref: this.router.urlFor(
          'apps.app.reports.custom-reports.report.show',
          event.value.report_id,
        ),
      });
    } else if (event.isReportDeleteEvent) {
      return this.intl.t('table-cells.admin-events.description-cell.report-delete', {
        ...this.defaultEventParams(event),
        title: event.value.before.title || this.intl.t('reporting.custom-reports.report.untitled'),
      });
    } else if (event.isReportingFolderCreateEvent) {
      return this.intl.t('table-cells.admin-events.description-cell.reporting-folder-create', {
        ...this.defaultEventParams(event),
        name: event.value.name,
      });
    } else if (event.isReportingFolderDeleteEvent) {
      return this.intl.t('table-cells.admin-events.description-cell.reporting-folder-delete', {
        ...this.defaultEventParams(event),
        name: event.value.name,
      });
    } else if (event.isReportingFolderNameChange) {
      return this.intl.t('table-cells.admin-events.description-cell.reporting-folder-name-change', {
        ...this.defaultEventParams(event),
        before: event.value.before,
        after: event.value.after,
      });
    } else if (event.isReportingFolderPositionChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.reporting-folder-position-change',
        {
          ...this.defaultEventParams(event),
          name: event.value.name,
        },
      );
    } else if (event.isReportingFolderReportsChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.reporting-folder-reports-change',
        {
          ...this.defaultEventParams(event),
          name: event.value.name,
        },
      );
    } else if (event.isReportingFolderPinChange) {
      return this.intl.t('table-cells.admin-events.description-cell.reporting-folder-pin-change', {
        ...this.defaultEventParams(event),
        name: event.value.name,
        before: event.value.before,
        after: event.value.after,
      });
    } else if (event.isReportingCsvExport) {
      return this.intl.t('table-cells.admin-events.description-cell.reporting-csv-export', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isReportSnapshotCreateEvent) {
      return this.intl.t('table-cells.admin-events.description-cell.report-snapshot-create', {
        ...this.defaultEventParams(event),
        title: event.value.report_name,
        reportHref: this.router.urlFor(
          'apps.app.reports.custom-reports.report.show',
          event.value.report_id,
        ),
      });
    } else if (event.isReportScheduledShareCreate) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.report-scheduled-share-create',
        {
          ...this.defaultEventParams(event),
          title: event.value.report_name,
          reportHref: this.router.urlFor(
            'apps.app.reports.custom-reports.report.show',
            event.value.report_id,
          ),
          scheduleId: event.value.schedule_id,
        },
      );
    } else if (event.isReportScheduledShareUpdate) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.report-scheduled-share-update',
        {
          ...this.defaultEventParams(event),
          title: event.value.report_name,
          reportHref: this.router.urlFor(
            'apps.app.reports.custom-reports.report.show',
            event.value.report_id,
          ),
          scheduleId: event.value.schedule_id,
        },
      );
    } else if (event.isReportScheduledShareDelete) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.report-scheduled-share-delete',
        {
          ...this.defaultEventParams(event),
          title: event.value.report_name,
          reportHref: this.router.urlFor(
            'apps.app.reports.custom-reports.report.show',
            event.value.report_id,
          ),
          scheduleId: event.value.schedule_id,
        },
      );
    } else if (event.isReportAccessCreate) {
      return this.intl.t('table-cells.admin-events.description-cell.report-access-create', {
        ...this.defaultEventParams(event),
        ...this.reportAccessParams(event),
      });
    } else if (event.isReportAccessDelete) {
      return this.intl.t('table-cells.admin-events.description-cell.report-access-delete', {
        ...this.defaultEventParams(event),
        ...this.reportAccessParams(event),
      });
    } else if (event.isReportAccessUpdate) {
      return this.intl.t('table-cells.admin-events.description-cell.report-access-update', {
        ...this.defaultEventParams(event),
        ...this.reportAccessParams(event),
        before: this.reportAccessService.getAccessTypeTranslation(event.value.before),
        after: this.reportAccessService.getAccessTypeTranslation(event.value.after),
      });
    } else if (event.isSeatRevoke) {
      return this.intl.t('table-cells.admin-events.description-cell.seat-revoke', {
        ...this.defaultEventParams(event),
        href1: this.adminUrl(event.value.admin_id),
        eventValueAdmin_name: event.value.admin_name,
        eventValueRevoked: formatArrayToSentence.compute([event.value.revoked]),
      });
    } else if (event.isAdminChannelChange) {
      let translationKey;
      if (event.value.auto_changed) {
        translationKey = 'table-cells.admin-events.description-cell.admin-channel-auto-change';
      } else if (event.value.update_by && event.value.update_by !== event.admin_id) {
        translationKey = 'table-cells.admin-events.description-cell.admin-channel-change-by-other';
      } else {
        translationKey = 'table-cells.admin-events.description-cell.admin-channel-change';
      }

      return this.intl.t(translationKey, {
        ...this.defaultEventParams(event),
        channel: this.channelAvailabilityToString(event.value.channel_availability),
        updatedBy: event.value.update_by_name,
      });
    } else if (event.isFinPersistedDataContextAttributeChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.fin-persisted-data-context-attribute-change',
        {
          ...this.defaultEventParams(event),
          action: event.value.action,
          friendly_attribute_type: event.value.friendly_attribute_type,
          friendly_attribute_identifier: event.value.friendly_attribute_identifier,
        },
      );
    } else if (event.isAiAgentCustomizationSettingsChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.ai-agent-customization-settings-change',
        {
          ...this.defaultEventParams(event),
        },
      );
    } else if (event.isAiAgentIdentitySettingsChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.ai-agent-identity-settings-change',
        {
          ...this.defaultEventParams(event),
        },
      );
    } else if (event.isAiAgentMultilingualSettingsChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.ai-agent-multilingual-settings-change',
        {
          ...this.defaultEventParams(event),
        },
      );
    } else if (event.isAiAgentGuidanceChange) {
      if (event.value.guideline_title == null) {
        return this.intl.t('table-cells.admin-events.description-cell.ai-agent-guidance-change', {
          ...this.defaultEventParams(event),
          action: event.value.action,
          href1: `/a/apps/${this.app.id}/automation/fin-ai-agent/guidance`,
        });
      } else if (event.value.action === 'deleted' || event.value.action === 'updated') {
        return this.intl.t(
          'table-cells.admin-events.description-cell.ai-agent-live-guidance-change-with-title',
          {
            ...this.defaultEventParams(event),
            action: event.value.action,
            guideline_title: event.value.guideline_title,
            href1: `/a/apps/${this.app.id}/automation/fin-ai-agent/guidance`,
          },
        );
      } else {
        return this.intl.t(
          'table-cells.admin-events.description-cell.ai-agent-guidance-change-with-title',
          {
            ...this.defaultEventParams(event),
            action: event.value.action,
            guideline_title: event.value.guideline_title,
            href1: `/a/apps/${this.app.id}/automation/fin-ai-agent/guidance`,
          },
        );
      }
    } else if (event.isAiAgentFinPlaygroundChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.ai-agent-fin-playground-change',
        {
          ...this.defaultEventParams(event),
          action: event.value.action,
          href1: this.app.canUseStandalone
            ? `/a/apps/${this.app.id}/standalone/testing`
            : `/a/apps/${this.app.id}/automation/fin-ai-agent/playground`,
        },
      );
    } else if (event.isAiAgentPreviewTokenGenerated) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.ai-agent-preview-token-generated',
        {
          ...this.defaultEventParams(event),
          allowed_origin: event.value?.allowed_origin,
        },
      );
    } else if (event.isMessengerLanguageChange) {
      return this.intl.t('table-cells.admin-events.description-cell.messenger-language-change', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isBrandCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.brand-creation', {
        ...this.defaultEventParams(event),
        eventValueBrand_name: event.value.brand_name,
      });
    } else if (event.isBrandUpdate) {
      return this.intl.t('table-cells.admin-events.description-cell.brand-update', {
        ...this.defaultEventParams(event),
        eventValueBrand_name: event.value.brand_name,
      });
    } else if (event.isSeriesCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.series-creation', {
        ...this.defaultEventParams(event),
        seriesHref: this.router.urlFor('apps.app.outbound.series.series', event.value.series_id),
        eventValueSeries_title:
          event.value.series_title ??
          this.intl.t('table-cells.admin-events.description-cell.series-untitled'),
      });
    } else if (event.isSeriesUpdate) {
      return this.intl.t('table-cells.admin-events.description-cell.series-update', {
        ...this.defaultEventParams(event),
        seriesHref: this.router.urlFor('apps.app.outbound.series.series', event.value.series_id),
        eventValueSeries_title:
          event.value.series_title ??
          this.intl.t('table-cells.admin-events.description-cell.series-untitled'),
      });
    } else if (event.isSeriesSettingsUpdate) {
      return this.intl.t('table-cells.admin-events.description-cell.series-settings-update', {
        ...this.defaultEventParams(event),
        seriesHref: this.router.urlFor('apps.app.outbound.series.series', event.value.series_id),
        eventValueSeries_title:
          event.value.series_title ??
          this.intl.t('table-cells.admin-events.description-cell.series-untitled'),
      });
    } else if (event.isSeriesDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.series-deletion', {
        ...this.defaultEventParams(event),
        eventValueSeries_title:
          event.value.series_title ??
          this.intl.t('table-cells.admin-events.description-cell.series-untitled'),
      });
    } else if (event.isSeriesStatusChange) {
      return this.intl.t('table-cells.admin-events.description-cell.series-status-change', {
        ...this.defaultEventParams(event),
        seriesHref: this.router.urlFor('apps.app.outbound.series.series', event.value.series_id),
        eventValueSeries_title:
          event.value.series_title ??
          this.intl.t('table-cells.admin-events.description-cell.series-untitled'),
        eventValueStatus: event.value.status,
      });
    } else if (event.isBrandDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.brand-deletion', {
        ...this.defaultEventParams(event),
        eventValueBrand_name: event.value.brand_name,
      });
    } else if (event.isActionRiskAcceptedChange) {
      return this.intl.t('table-cells.admin-events.description-cell.action-risk-accepted-change', {
        ...this.defaultEventParams(event),
        actionName: event.value.action_name,
        actionHref: this.router.urlFor(
          'apps.app.settings.app-settings.custom-actions.custom-action',
          event.value.action_id,
        ),
      });
    } else if (event.isActionPublished) {
      return this.intl.t('table-cells.admin-events.description-cell.action-published', {
        ...this.defaultEventParams(event),
        actionName: event.value.action_name,
        actionHref: this.router.urlFor(
          'apps.app.settings.app-settings.custom-actions.custom-action',
          event.value.action_id,
        ),
      });
    } else if (event.isActionStatusChange) {
      return this.intl.t('table-cells.admin-events.description-cell.action-status-change', {
        ...this.defaultEventParams(event),
        actionName: event.value.action_name,
        actionHref: this.router.urlFor(
          'apps.app.settings.app-settings.custom-actions.custom-action',
          event.value.action_id,
        ),
        eventValueStatus: event.value.status,
      });
    } else if (event.isActionUpdate) {
      return this.intl.t('table-cells.admin-events.description-cell.action-update', {
        ...this.defaultEventParams(event),
        actionName: event.value.action_name,
        actionHref: this.router.urlFor(
          'apps.app.settings.app-settings.custom-actions.custom-action',
          event.value.action_id,
        ),
      });
    } else if (event.isSelectionSetCreation) {
      let contentType = event.value.content_type;
      let contentTypeLabel = contentType ? this.rulesetContentTypeLabel(contentType) : contentType;
      let params = {
        ...this.defaultEventParams(event),
        selectionSetHref: this.outboundSelectionSetUrl(event),
        eventValueSelectionSet_contentType: contentTypeLabel,
      };
      return this.intl.t('table-cells.admin-events.description-cell.selection-set-creation', {
        ...params,
      });
    } else if (event.isSelectionSetArchive) {
      let contentType = event.value.content_type;
      let contentTypeLabel = contentType ? this.rulesetContentTypeLabel(contentType) : contentType;
      let params = {
        ...this.defaultEventParams(event),
        selectionSetHref: this.outboundSelectionSetUrl(event),
        eventValueSelectionSet_contentType: contentTypeLabel,
      };
      return this.intl.t('table-cells.admin-events.description-cell.selection-set-archive', {
        ...params,
      });
    } else if (event.isMessengerLaunchToConversationChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.messenger-launch-to-conversation-change',
        {
          ...this.defaultEventParams(event),
          action: event.value.enabled ? 'enabled' : 'disabled',
          audience: event.value.audience,
        },
      );
    } else if (event.isAliasModeChange) {
      if (event.value.alias_enabled) {
        return this.intl.t('table-cells.admin-events.description-cell.alias-enabled', {
          ...this.defaultEventParams(event),
          eventValueAlias_enabled: event.value.alias_enabled,
        });
      } else {
        return this.intl.t('table-cells.admin-events.description-cell.alias-disabled', {
          ...this.defaultEventParams(event),
          eventValueAlias_enabled: event.value.alias_enabled,
        });
      }
    } else if (event.isHippaComplianceSettingsChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.hippa-compliance-settings-change',
        {
          ...this.defaultEventParams(event),
          action: event.value.enabled ? 'enabled' : 'disabled',
        },
      );
    } else if (event.isAiInboxTranslationSettingChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.ai-inbox-translation-settings-change',
        {
          ...this.defaultEventParams(event),
          action: event.value.enabled ? 'enabled' : 'disabled',
        },
      );
    } else if (event.isEmailSettingsChange) {
      if (event.value.setting_type === EMAIL_SETTING_TYPES.EMAIL_AUTO_REPLY) {
        // Toggle on/off
        return this.intl.t('table-cells.admin-events.description-cell.email-auto-reply-toggle', {
          ...this.defaultEventParams(event),
          action: event.value.updated_setting === 'true' ? 'enabled' : 'disabled',
        });
      } else if (event.value.setting_type === EMAIL_SETTING_TYPES.EMAIL_AUTO_REPLY_TEXT) {
        // Text content change - check if language is available in context
        let language = event.value.context?.language ?? 'default';
        return this.intl.t(
          'table-cells.admin-events.description-cell.email-auto-reply-text-change',
          {
            ...this.defaultEventParams(event),
            language,
          },
        );
      } else if (event.value.setting_type === EMAIL_SETTING_TYPES.USER_EMAIL_FOLLOW_UPS) {
        // Conversation updates email notification setting
        return this.intl.t(
          'table-cells.admin-events.description-cell.user-email-follow-ups-change',
          {
            ...this.defaultEventParams(event),
            action: event.value.updated_setting === 'true' ? 'enabled' : 'disabled',
          },
        );
      } else if (event.value.setting_type === EMAIL_SETTING_TYPES.USER_TICKET_EMAIL_FOLLOW_UPS) {
        // Ticket updates email notification setting
        return this.intl.t(
          'table-cells.admin-events.description-cell.user-ticket-email-follow-ups-change',
          {
            ...this.defaultEventParams(event),
            action: event.value.updated_setting === 'true' ? 'enabled' : 'disabled',
          },
        );
      } else if (
        event.value.setting_type === EMAIL_SETTING_TYPES.EMAIL_NOTIFICATION_DESTINATION_URL
      ) {
        // Email notification destination URL setting
        if (
          event.value.updated_setting &&
          event.value.updated_setting !== 'null' &&
          event.value.updated_setting.trim().length > 0
        ) {
          // URL was set to a specific value
          return this.intl.t(
            'table-cells.admin-events.description-cell.email-notification-destination-url-set',
            {
              ...this.defaultEventParams(event),
              destinationUrl: event.value.updated_setting,
            },
          );
        } else {
          // URL was cleared (null or empty)
          return this.intl.t(
            'table-cells.admin-events.description-cell.email-notification-destination-url-cleared',
            {
              ...this.defaultEventParams(event),
            },
          );
        }
      } else {
        // Fallback for any other email settings
        return this.intl.t('table-cells.admin-events.description-cell.email-settings-change', {
          ...this.defaultEventParams(event),
          settingName: event.value.setting_type,
        });
      }
    } else if (event.isStripInboundEmailLinksChange) {
      let action = event.value.strip_enabled ? 'enabled' : 'disabled';
      return this.intl.t(
        'table-cells.admin-events.description-cell.strip-inbound-email-links-change',
        {
          ...this.defaultEventParams(event),
          action,
        },
      );
    } else if (event.isContentSuggestionsChange) {
      // Calculate status from enabled_for_users and enabled_for_visitors booleans
      let status;
      let enabledForUsers = event.value.enabled_for_users;
      let enabledForVisitors = event.value.enabled_for_visitors;

      if (enabledForUsers && enabledForVisitors) {
        status = this.intl.t(
          'table-cells.admin-events.description-cell.content-suggestions-status.enabled-for-users-and-visitors',
        );
      } else if (enabledForUsers) {
        status = this.intl.t(
          'table-cells.admin-events.description-cell.content-suggestions-status.enabled-for-users-only',
        );
      } else if (enabledForVisitors) {
        status = this.intl.t(
          'table-cells.admin-events.description-cell.content-suggestions-status.enabled-for-visitors-only',
        );
      } else {
        status = this.intl.t(
          'table-cells.admin-events.description-cell.content-suggestions-status.disabled',
        );
      }

      return this.intl.t('table-cells.admin-events.description-cell.content-suggestions-change', {
        ...this.defaultEventParams(event),
        status,
      });
    } else if (event.isAiCategoryChange) {
      let actionKey = event.value.action === 'enabled' ? 'enabled' : 'disabled';
      let action = this.intl.t(
        `table-cells.admin-events.description-cell.ai-category-status.${actionKey}`,
      );
      return this.intl.t('table-cells.admin-events.description-cell.ai-category-change', {
        ...this.defaultEventParams(event),
        action,
        categoryName: event.value.category_name,
      });
    } else if (event.isCustomAuthenticationTokenCreation) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.custom-authentication-token-creation',
        {
          ...this.defaultEventParams(event),
          eventValueTokenName: event.value.token_name || event.value.name,
          eventValueTokenType: event.value.token_type || event.value.type,
        },
      );
    } else if (event.isEscalationRuleChange) {
      let actionKey = event.value.action === 'enabled' ? 'enabled' : 'disabled';
      let action = this.intl.t(
        `table-cells.admin-events.description-cell.escalation-rule-status.${actionKey}`,
      );
      return this.intl.t('table-cells.admin-events.description-cell.escalation-rule-change', {
        ...this.defaultEventParams(event),
        action,
        ruleName: event.value.rule_name,
      });
    } else if (event.isMessengerApiSecretCreation) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.messenger-api-secret-creation',
        {
          ...this.defaultEventParams(event),
          secretName: event.value.secret_name || event.value.name,
          platforms: formatArrayToSentence.compute([event.value.platforms]),
        },
      );
    } else if (event.isMessengerApiSecretDeletion) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.messenger-api-secret-deletion',
        {
          ...this.defaultEventParams(event),
          secretName: event.value.secret_name || event.value.name,
          platforms: formatArrayToSentence.compute([event.value.platforms]),
        },
      );
    } else if (event.isWorkspaceDeletionRequest) {
      return this.intl.t('table-cells.admin-events.description-cell.workspace-deletion-request', {
        ...this.defaultEventParams(event),
        workspaceName: event.value?.workspace_name || event.value?.name,
      });
    } else if (event.isDeveloperHubSecretRevealed) {
      let secretLabel = (
        event.value.label ||
        (event.value.secret_type && event.value.secret_type.replace(/_/g, ' ')) ||
        'secret'
      ).toLowerCase();
      let appName = event.value.app_package_name || event.value.app_package_id;
      return this.intl.t(
        'table-cells.admin-events.description-cell.developer-hub-secret-revealed',
        {
          ...this.defaultEventParams(event),
          secretLabel,
          appName,
          appPackageId: event.value.app_package_id,
        },
      );
    } else if (event.isPiiDataRevealed) {
      return this.intl.t('table-cells.admin-events.description-cell.pii-data-revealed', {
        ...this.defaultEventParams(event),
        eventValueField: event.value.field,
        eventValueUser_id: event.value.user_id,
      });
    } else if (event.isDeveloperHubSecretCopied) {
      let secretLabel = (
        event.value.label ||
        (event.value.secret_type && event.value.secret_type.replace(/_/g, ' ')) ||
        'secret'
      ).toLowerCase();
      let appName = event.value.app_package_name || event.value.app_package_id;
      return this.intl.t('table-cells.admin-events.description-cell.developer-hub-secret-copied', {
        ...this.defaultEventParams(event),
        secretLabel,
        appName,
        appPackageId: event.value.app_package_id,
      });
    } else if (event.isCustomEmojiDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.custom-emoji-deletion', {
        ...this.defaultEventParams(event),
        eventValueCustomEmoji_id: event.value.custom_emoji_id,
        eventValueCustomEmoji_name: event.value.custom_emoji_name,
        eventValueSmall_asset_url: event.value.small_asset_url,
      });
    } else if (event.isAttachUploadsInlineSettingChange) {
      let action = event.value.enabled ? 'enabled' : 'disabled';
      return this.intl.t(
        'table-cells.admin-events.description-cell.attach-uploads-inline-setting-change',
        {
          ...this.defaultEventParams(event),
          action,
        },
      );
    } else if (event.isUserCameraAttachmentsSettingChange) {
      let action = event.value.enabled ? 'enabled' : 'disabled';
      return this.intl.t(
        'table-cells.admin-events.description-cell.user-camera-attachments-setting-change',
        {
          ...this.defaultEventParams(event),
          action,
        },
      );
    } else if (event.isUserMediaAttachmentsSettingChange) {
      let action = event.value.enabled ? 'enabled' : 'disabled';
      return this.intl.t(
        'table-cells.admin-events.description-cell.user-media-attachments-setting-change',
        {
          ...this.defaultEventParams(event),
          action,
        },
      );
    } else if (event.isUserFileAttachmentsSettingChange) {
      let action = event.value.enabled ? 'enabled' : 'disabled';
      return this.intl.t(
        'table-cells.admin-events.description-cell.user-file-attachments-setting-change',
        {
          ...this.defaultEventParams(event),
          action,
        },
      );
    } else if (event.isUserGifsSettingChange) {
      let action = event.value.enabled ? 'enabled' : 'disabled';
      return this.intl.t('table-cells.admin-events.description-cell.user-gifs-setting-change', {
        ...this.defaultEventParams(event),
        action,
      });
    } else if (event.isUserVoiceNotesSettingChange) {
      let action = event.value.enabled ? 'enabled' : 'disabled';
      return this.intl.t(
        'table-cells.admin-events.description-cell.user-voice-notes-setting-change',
        {
          ...this.defaultEventParams(event),
          action,
        },
      );
    } else if (event.isUserConversationAttachmentsSettingChange) {
      let action = event.value.enabled ? 'enabled' : 'disabled';
      return this.intl.t(
        'table-cells.admin-events.description-cell.user-conversation-attachments-setting-change',
        {
          ...this.defaultEventParams(event),
          action,
        },
      );
    } else if (event.isTeammateGifsSettingChange) {
      let action = event.value.enabled ? 'enabled' : 'disabled';
      return this.intl.t('table-cells.admin-events.description-cell.teammate-gifs-setting-change', {
        ...this.defaultEventParams(event),
        action,
      });
    } else if (event.isAllowedAttachmentFiletypesSettingChange) {
      let action = event.value.enabled ? 'enabled' : 'disabled';
      let filetypeList = event.value.filetype_list || '';
      let filetypeExtensions = filetypeList
        ? this.intl.t('table-cells.admin-events.description-cell.with-extensions', { filetypeList })
        : '';
      return this.intl.t(
        'table-cells.admin-events.description-cell.allowed-attachment-filetypes-setting-change',
        {
          ...this.defaultEventParams(event),
          action,
          filetypeExtensions,
        },
      );
    } else if (event.isUnassignUnsnoozedAtCapacitySettingChange) {
      let action = event.value.enabled ? 'enabled' : 'disabled';
      return this.intl.t(
        'table-cells.admin-events.description-cell.unassign-unsnoozed-at-capacity-setting-change',
        {
          ...this.defaultEventParams(event),
          action,
        },
      );
    } else if (event.isUnassignUnsnoozedWhenAwaySettingChange) {
      if (event.value.enabled) {
        let mode = event.value.mode || 'when_away_only';
        let key =
          mode === 'when_away_and_reassign'
            ? 'table-cells.admin-events.description-cell.unassign-unsnoozed-when-away-setting-change-enabled-when-away-and-reassign'
            : 'table-cells.admin-events.description-cell.unassign-unsnoozed-when-away-setting-change-enabled-when-away-only';
        return this.intl.t(key, { ...this.defaultEventParams(event) });
      } else {
        return this.intl.t(
          'table-cells.admin-events.description-cell.unassign-unsnoozed-when-away-setting-change-disabled',
          { ...this.defaultEventParams(event) },
        );
      }
    } else if (event.isAutomaticAwayModeSettingChange) {
      return this.formatAutomaticAwayModeSettingChangeEvent(event);
    } else if (event.isFinAgentApiSettingsChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.fin-agent-api-settings-change',
        {
          ...this.defaultEventParams(event),
        },
      );
    } else if (event.isScimGroupCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.scim-group-creation', {
        ...this.defaultEventParams(event),
        eventValueGroupName: event.value?.group_name,
      });
    } else if (event.isScimGroupUpdate) {
      return this.formatScimGroupUpdateEvent(event);
    } else if (event.isScimGroupDeletion) {
      return this.formatScimGroupDeletionEvent(event);
    } else if (event.isScimAdminProfileUpdate) {
      let targetAdminId = event.value?.static?.admin_id;
      let targetAdminName = event.value?.after?.name || event.value?.before?.name;
      return this.intl.t('table-cells.admin-events.description-cell.scim-admin-profile-update', {
        ...this.defaultEventParams(event),
        eventValueAdminName: targetAdminName,
        href1: this.adminUrl(targetAdminId),
      });
    } else if (event.isScimAdminPasswordUpdate) {
      let targetAdminId = event.value?.admin_id;
      let targetAdminName = event.value?.admin_name;
      return this.intl.t('table-cells.admin-events.description-cell.scim-admin-password-update', {
        ...this.defaultEventParams(event),
        eventValueAdminName: targetAdminName,
        href1: this.adminUrl(targetAdminId),
      });
    } else if (event.isScimSystemEnabled) {
      return this.intl.t('table-cells.admin-events.description-cell.scim-system-enabled', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isScimSystemDisabled) {
      return this.intl.t('table-cells.admin-events.description-cell.scim-system-disabled', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isScimSettingsChange) {
      return this.intl.t('table-cells.admin-events.description-cell.scim-settings-change', {
        ...this.defaultEventParams(event),
      });
    } else if (event.isScimGroupMembershipChange) {
      let operation = event.value?.operation;
      // Translate the operation to the correct past tense verb for the current locale
      let translatedOperation = operation;
      if (operation === 'add' || operation === 'remove') {
        translatedOperation = this.intl.t(
          `table-cells.admin-events.scim-group-membership-operation.${operation}`,
        );
      }

      // Support both 'count' (new) and 'admin_count' (legacy) for backward compatibility
      let adminCount = event.value?.count ?? event.value?.admin_count;

      return this.intl.t('table-cells.admin-events.description-cell.scim-group-membership-change', {
        ...this.defaultEventParams(event),
        eventValueOperation: translatedOperation,
        eventValueGroupName: event.value?.group_name,
        eventValueAdminCount: adminCount,
      });
    } else if (event.isScimAdminRoleChange) {
      return this.formatScimAdminRoleChangeEvent(event);
    } else if (event.isScimGroupsBulkUpdate) {
      return this.formatScimGroupsBulkUpdateEvent(event);
    } else if (event.isScimAdminBulkRoleAssignment) {
      return this.formatScimAdminBulkRoleAssignmentEvent(event);
    } else if (event.isMacroCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.macro-creation', {
        ...this.defaultEventParams(event),
        macroName: event.value?.name || event.value?.macro_name || event.value?.title,
        visibility: event.value?.visibility || event.value?.macro_visibility,
      });
    } else if (event.isMacroUpdate) {
      if (event.value?.static?.macro_id) {
        let macroId = event.value?.static?.macro_id;
        let macroName = event.value?.static?.macro_name;
        let macroHref = macroId ? this.macroEditorUrl(macroId) : null;

        return this.intl.t('table-cells.admin-events.description-cell.macro-update.v1', {
          ...this.defaultEventParams(event),
          macroName,
          macroHref,
        });
      } else {
        return this.intl.t('table-cells.admin-events.description-cell.macro-update.v0', {
          ...this.defaultEventParams(event),
          macroName: event.value?.macro_name,
          visibility: event.value?.visibility,
        });
      }
    } else if (event.isMacroDeletion) {
      return this.intl.t('table-cells.admin-events.description-cell.macro-deletion', {
        ...this.defaultEventParams(event),
        macroName: event.value?.name || event.value?.macro_name || event.value?.title,
      });
    } else if (event.isMcpServerCreated) {
      return this.intl.t('table-cells.admin-events.description-cell.mcp-server-created', {
        ...this.defaultEventParams(event),
        serverName: event.value.server_name,
        authenticationType: event.value.authentication_type,
      });
    } else if (event.isMcpServerUpdated) {
      let changes = event.value.changes;
      let changeSummary = changes ? Object.keys(changes).join(', ') : '';
      return this.intl.t('table-cells.admin-events.description-cell.mcp-server-updated', {
        ...this.defaultEventParams(event),
        serverName: event.value.server_name,
        changeSummary,
      });
    } else if (event.isMcpServerDeleted) {
      return this.intl.t('table-cells.admin-events.description-cell.mcp-server-deleted', {
        ...this.defaultEventParams(event),
        serverName: event.value.server_name,
      });
    } else if (event.isMcpServerEnabled) {
      return this.intl.t('table-cells.admin-events.description-cell.mcp-server-enabled', {
        ...this.defaultEventParams(event),
        serverName: event.value.server_name,
      });
    } else if (event.isMcpServerDisabled) {
      return this.intl.t('table-cells.admin-events.description-cell.mcp-server-disabled', {
        ...this.defaultEventParams(event),
        serverName: event.value.server_name,
      });
    } else if (event.isMcpServerOauthConnected) {
      return this.intl.t('table-cells.admin-events.description-cell.mcp-server-oauth-connected', {
        ...this.defaultEventParams(event),
        serverName: event.value.server_name,
      });
    } else if (event.isMcpServerOauthDisconnected) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.mcp-server-oauth-disconnected',
        {
          ...this.defaultEventParams(event),
          serverName: event.value.server_name,
        },
      );
    } else if (event.isMcpActionAdded) {
      return this.intl.t('table-cells.admin-events.description-cell.mcp-action-added', {
        ...this.defaultEventParams(event),
        actionName: event.value.action_name,
        serverName: event.value.server_name,
      });
    } else if (event.isMcpActionsBatchAdded) {
      return this.intl.t('table-cells.admin-events.description-cell.mcp-actions-batch-added', {
        ...this.defaultEventParams(event),
        actionsCount: event.value.actions_count,
        serverName: event.value.server_name,
      });
    } else if (event.isMcpActionUpdated) {
      let changes = event.value.changes;
      let changeSummary = changes ? Object.keys(changes).join(', ') : '';
      return this.intl.t('table-cells.admin-events.description-cell.mcp-action-updated', {
        ...this.defaultEventParams(event),
        actionName: event.value.action_name,
        changeSummary,
      });
    } else if (event.isMcpActionRemoved) {
      return this.intl.t('table-cells.admin-events.description-cell.mcp-action-removed', {
        ...this.defaultEventParams(event),
        actionName: event.value.action_name,
        serverName: event.value.server_name,
      });
    } else if (event.isQaMonitorCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.qa-monitor-creation', {
        ...this.defaultEventParams(event),
        monitorName: event.value?.monitor_name,
      });
    } else if (event.isQaMonitorUpdate) {
      return this.intl.t('table-cells.admin-events.description-cell.qa-monitor-update', {
        ...this.defaultEventParams(event),
        monitorName: event.value?.monitor_name,
      });
    } else if (event.isQaMonitorArchive) {
      return this.intl.t('table-cells.admin-events.description-cell.qa-monitor-archive', {
        ...this.defaultEventParams(event),
        monitorName: event.value?.monitor_name,
      });
    } else if (event.isQaScorecardCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.qa-scorecard-creation', {
        ...this.defaultEventParams(event),
        scorecardName: event.value?.scorecard_name,
      });
    } else if (event.isQaScorecardUpdate) {
      return this.intl.t('table-cells.admin-events.description-cell.qa-scorecard-update', {
        ...this.defaultEventParams(event),
        scorecardName: event.value?.scorecard_name,
      });
    } else if (event.isQaScorecardArchive) {
      return this.intl.t('table-cells.admin-events.description-cell.qa-scorecard-archive', {
        ...this.defaultEventParams(event),
        scorecardName: event.value?.scorecard_name,
      });
    } else if (event.isLeadToUserMerge) {
      return this.intl.t('table-cells.admin-events.description-cell.lead-to-user-merge', {
        ...this.defaultEventParams(event),
        sourceUserId: event.value?.source_user_id,
        targetUserId: event.value?.target_user_id,
        targetUserHref: `/a/apps/${this.app.id}/users/${event.value?.target_user_id}`,
      });
    } else if (event.isShopifyRefundOrder) {
      return this.intl.t('table-cells.admin-events.description-cell.shopify-refund-order', {
        ...this.defaultEventParams(event),
        orderName: event.value.order_name || event.value.order_id,
        orderId: event.value.order_id,
        shopifyDomain: event.value.shopify_domain,
        conversationId: event.value.conversation_id,
        conversationHref: this.conversationUrl(event.value.conversation_id),
        refundType: event.value.refund_type,
        itemsSummary: this.formatLineItemsSummary(event.value.line_items_detail),
        note: event.value.note || '',
      });
    } else if (event.isShopifyReplacementOrder) {
      return this.intl.t('table-cells.admin-events.description-cell.shopify-replacement-order', {
        ...this.defaultEventParams(event),
        orderName: event.value.order_name || event.value.order_id,
        orderId: event.value.order_id,
        shopifyDomain: event.value.shopify_domain,
        conversationId: event.value.conversation_id,
        conversationHref: this.conversationUrl(event.value.conversation_id),
        reason: event.value.reason,
        newOrderId: event.value.new_order_id,
        itemsSummary: this.formatLineItemsSummary(event.value.line_items_detail),
      });
    } else if (event.isShopifyGiftCardCompensation) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.shopify-gift-card-compensation',
        {
          ...this.defaultEventParams(event),
          orderName: event.value.order_name || event.value.order_id,
          orderId: event.value.order_id,
          shopifyDomain: event.value.shopify_domain,
          conversationId: event.value.conversation_id,
          conversationHref: this.conversationUrl(event.value.conversation_id),
          giftCardValue: event.value.gift_card_value,
          reason: event.value.reason,
          giftCardCode: event.value.gift_card_code || '',
          expiryDate: this.formatDate(event.value.expiry_date),
          itemsSummary: this.formatLineItemsSummary(event.value.line_items_detail),
        },
      );
    } else if (event.isShopifyAddReplacementItem) {
      return this.intl.t('table-cells.admin-events.description-cell.shopify-add-replacement-item', {
        ...this.defaultEventParams(event),
        orderName: event.value.order_name || event.value.order_id,
        orderId: event.value.order_id,
        shopifyDomain: event.value.shopify_domain,
        conversationId: event.value.conversation_id,
        conversationHref: this.conversationUrl(event.value.conversation_id),
        reason: event.value.reason,
        lineItemTitle: event.value.line_item_title || event.value.line_item_id,
        quantity: event.value.quantity,
      });
    } else if (event.isCsvImportCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.csv-import-creation', {
        ...this.defaultEventParams(event),
        eventValueRow_count: event.value.row_count,
        eventValueRecord_type: event.value.record_type,
      });
    } else if (event.isCsvImportCompletion) {
      return this.intl.t('table-cells.admin-events.description-cell.csv-import-completion', {
        ...this.defaultEventParams(event),
        eventValueUsers_created: event.value.users_created,
        eventValueUsers_updated: event.value.users_updated,
      });
    } else if (event.isConversationDeletionScheduleCreation) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.conversation-deletion-schedule-creation',
        {
          ...this.defaultEventParams(event),
          eventValueRetention_days: event.value.retention_days,
          eventValueState: event.value.state,
        },
      );
    } else if (event.isConversationDeletionScheduleStateChange) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.conversation-deletion-schedule-state-change',
        {
          ...this.defaultEventParams(event),
          eventValueBefore_state: event.value.before.state,
          eventValueAfter_state: event.value.after.state,
        },
      );
    } else if (event.isConversationDeletionScheduleUpdate) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.conversation-deletion-schedule-update',
        {
          ...this.defaultEventParams(event),
          eventValueBefore_retention_days: event.value.before.retention_days,
          eventValueAfter_retention_days: event.value.after.retention_days,
        },
      );
    } else if (event.isConversationDeletionScheduleDeletion) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.conversation-deletion-schedule-deletion',
        {
          ...this.defaultEventParams(event),
          eventValueRetention_days: event.value.retention_days,
        },
      );
    } else if (event.isContentRedactionRuleCreation) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.content-redaction-rule-creation',
        {
          ...this.defaultEventParams(event),
          eventValueRule_name: event.value?.rule_name,
          eventValueRule_type: event.value?.rule_type,
        },
      );
    } else if (event.isContentRedactionRuleUpdate) {
      let ruleName = event.value?.static?.rule_name;
      let ruleType = event.value?.static?.rule_type;
      return this.intl.t(
        'table-cells.admin-events.description-cell.content-redaction-rule-update',
        {
          ...this.defaultEventParams(event),
          eventValueRule_name: ruleName,
          eventValueRule_type: ruleType,
        },
      );
    } else if (event.isContentRedactionRuleDeletion) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.content-redaction-rule-deletion',
        {
          ...this.defaultEventParams(event),
          eventValueRule_name: event.value?.rule_name,
          eventValueRule_type: event.value?.rule_type,
        },
      );
    } else if (event.isConversationHardDeletionEnqueued) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.conversation-hard-deletion-enqueued',
        {
          ...this.defaultEventParams(event),
          eventValueConversation_id: event.value?.conversation_id,
        },
      );
    } else if (event.isConversationRedactionEnqueued) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.conversation-redaction-enqueued',
        {
          ...this.defaultEventParams(event),
          eventValueConversation_id: event.value?.conversation_id,
        },
      );
    } else if (event.isSlackOauthFreshInstall) {
      return this.intl.t('table-cells.admin-events.description-cell.slack-oauth-fresh-install', {
        ...this.defaultEventParams(event),
        eventValueSlack_team_name: event.value?.slack_event_team_name,
        eventValueSlack_team_id: event.value?.slack_event_team_id,
        eventValueIntegration_type: event.value?.integration_type,
      });
    } else if (event.isSlackOauthReinstall) {
      return this.intl.t('table-cells.admin-events.description-cell.slack-oauth-reinstall', {
        ...this.defaultEventParams(event),
        eventValueSlack_team_name: event.value?.slack_event_team_name,
        eventValueSlack_team_id: event.value?.slack_event_team_id,
        eventValueIntegration_type: event.value?.integration_type,
      });
    } else if (event.isSlackOauthReauthentication) {
      return this.intl.t('table-cells.admin-events.description-cell.slack-oauth-reauthentication', {
        ...this.defaultEventParams(event),
        eventValueSlack_team_name: event.value?.slack_event_team_name,
        eventValueSlack_team_id: event.value?.slack_event_team_id,
      });
    } else if (event.isSlackUserTokenCreation) {
      return this.intl.t('table-cells.admin-events.description-cell.slack-user-token-creation', {
        ...this.defaultEventParams(event),
        eventValueSlack_team_name: event.value?.slack_event_team_name,
      });
    }
  }

  channelAvailabilityToString(channelAvailability) {
    return this.intl.t(
      `table-cells.admin-events.description-cell.channel-availability.${channelAvailability}`,
    );
  }

  formatLineItemsSummary(lineItemsDetail) {
    if (!lineItemsDetail || !Array.isArray(lineItemsDetail) || lineItemsDetail.length === 0) {
      return '';
    }
    return lineItemsDetail
      .map((item) => {
        let title =
          item.title ||
          this.intl.t('table-cells.admin-events.description-cell.shopify-unknown-item');
        let qty = item.quantity || 0;
        return `${title} (x${qty})`;
      })
      .join(', ');
  }

  formatScimGroupUpdateEvent(event) {
    let changes;
    let eventValueGroupName;
    let nameChanged;
    let membersChanged;
    let priorityChanged;
    let memberSummary;

    changes = event.value?.changes || {};
    eventValueGroupName = event.value?.group_name;
    nameChanged = changes.name?.from !== undefined && changes.name?.to !== undefined;
    membersChanged = changes.members && changes.members.length > 0;
    priorityChanged = changes.priority?.from !== undefined && changes.priority?.to !== undefined;

    if (nameChanged && membersChanged) {
      memberSummary = this.buildMemberChangeSummary(changes.members);
      return this.intl.t(
        'table-cells.admin-events.description-cell.scim-group-update-name-and-members',
        {
          ...this.defaultEventParams(event),
          eventValueOldName: changes.name.from,
          eventValueNewName: changes.name.to,
          memberSummary,
        },
      );
    } else if (nameChanged) {
      return this.intl.t('table-cells.admin-events.description-cell.scim-group-update-name-only', {
        ...this.defaultEventParams(event),
        eventValueOldName: changes.name.from,
        eventValueNewName: changes.name.to,
      });
    } else if (membersChanged) {
      memberSummary = this.buildMemberChangeSummary(changes.members);
      return this.intl.t(
        'table-cells.admin-events.description-cell.scim-group-update-members-only',
        {
          ...this.defaultEventParams(event),
          eventValueGroupName,
          memberSummary,
        },
      );
    } else if (priorityChanged) {
      return this.intl.t('table-cells.admin-events.description-cell.scim-group-update-priority', {
        ...this.defaultEventParams(event),
        eventValueGroupName,
        eventValueOldPriority: changes.priority.from,
        eventValueNewPriority: changes.priority.to,
      });
    } else {
      return this.intl.t('table-cells.admin-events.description-cell.scim-group-update', {
        ...this.defaultEventParams(event),
        eventValueGroupName,
      });
    }
  }

  formatScimGroupDeletionEvent(event) {
    let eventValueGroupName = event.value?.group_name;
    let eventValueMemberCount = event.value?.member_count;

    if (eventValueMemberCount && eventValueMemberCount > 0) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.scim-group-deletion-with-members',
        {
          ...this.defaultEventParams(event),
          eventValueGroupName,
          eventValueMemberCount,
        },
      );
    } else {
      return this.intl.t('table-cells.admin-events.description-cell.scim-group-deletion', {
        ...this.defaultEventParams(event),
        eventValueGroupName,
      });
    }
  }

  formatAutomaticAwayModeSettingChangeEvent(event) {
    let { before, after } = event.value || {};

    let initialActiveChanged =
      before?.initial_inactivity_threshold_active !== undefined &&
      after?.initial_inactivity_threshold_active !== undefined &&
      before.initial_inactivity_threshold_active !== after.initial_inactivity_threshold_active;

    let followingActiveChanged =
      before?.following_inactivity_threshold_active !== undefined &&
      after?.following_inactivity_threshold_active !== undefined &&
      before.following_inactivity_threshold_active !== after.following_inactivity_threshold_active;

    let anyToggleChanged = initialActiveChanged || followingActiveChanged;

    if (anyToggleChanged) {
      let wasEnabled = false;

      if (initialActiveChanged) {
        wasEnabled = after.initial_inactivity_threshold_active;
      } else if (followingActiveChanged) {
        wasEnabled = after.following_inactivity_threshold_active;
      }

      if (wasEnabled) {
        return this.intl.t(
          'table-cells.admin-events.description-cell.automatic-away-mode-setting-enabled',
          {
            ...this.defaultEventParams(event),
          },
        );
      } else {
        return this.intl.t(
          'table-cells.admin-events.description-cell.automatic-away-mode-setting-disabled',
          {
            ...this.defaultEventParams(event),
          },
        );
      }
    } else {
      return this.intl.t(
        'table-cells.admin-events.description-cell.automatic-away-mode-setting-change',
        {
          ...this.defaultEventParams(event),
        },
      );
    }
  }

  formatScimAdminRoleChangeEvent(event) {
    let eventValueAdminName = event.value?.static?.admin_name;
    let eventValueBeforeRoleName = event.value?.before?.role?.name;
    let eventValueAfterRoleName = event.value?.after?.role?.name;

    if (eventValueBeforeRoleName && eventValueAfterRoleName) {
      return this.intl.t('table-cells.admin-events.description-cell.scim-admin-role-change-full', {
        ...this.defaultEventParams(event),
        eventValueAdminName,
        eventValueBeforeRoleName,
        eventValueAfterRoleName,
      });
    } else if (eventValueAfterRoleName) {
      return this.intl.t('table-cells.admin-events.description-cell.scim-admin-role-assigned', {
        ...this.defaultEventParams(event),
        eventValueAdminName,
        eventValueAfterRoleName,
      });
    } else if (eventValueBeforeRoleName) {
      return this.intl.t('table-cells.admin-events.description-cell.scim-admin-role-removed', {
        ...this.defaultEventParams(event),
        eventValueAdminName,
        eventValueBeforeRoleName,
      });
    } else {
      return this.intl.t('table-cells.admin-events.description-cell.scim-admin-role-change', {
        ...this.defaultEventParams(event),
        eventValueAdminName,
      });
    }
  }

  buildMemberChangeSummary(memberChanges) {
    if (!Array.isArray(memberChanges)) {
      return this.intl.t('table-cells.admin-events.description-cell.membership-updated');
    }

    let addedCount = 0;
    let removedCount = 0;
    let replaced = false;

    memberChanges.forEach((change) => {
      switch (change.operation) {
        case 'add':
          addedCount += change.members?.length || 0;
          break;
        case 'remove':
          removedCount += change.members?.length || 0;
          break;
        case 'replace':
          replaced = true;
          break;
      }
    });

    let summaryParts;

    summaryParts = [];
    if (addedCount > 0) {
      summaryParts.push(
        this.intl.t('table-cells.admin-events.description-cell.members-added', {
          count: addedCount,
        }),
      );
    }
    if (removedCount > 0) {
      summaryParts.push(
        this.intl.t('table-cells.admin-events.description-cell.members-removed', {
          count: removedCount,
        }),
      );
    }
    if (replaced) {
      summaryParts.push(this.intl.t('table-cells.admin-events.description-cell.members-replaced'));
    }

    return summaryParts.length > 0
      ? summaryParts.join(', ')
      : this.intl.t('table-cells.admin-events.description-cell.membership-updated');
  }

  formatScimGroupsBulkUpdateEvent(event) {
    let groups = event.value?.groups || [];
    let totalChanged = event.value?.total_groups_changed || groups.length;

    if (groups.length === 0) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.scim-groups-bulk-update-empty',
        {
          ...this.defaultEventParams(event),
        },
      );
    }

    if (groups.length === 1) {
      let group = groups[0];
      return this.intl.t(
        'table-cells.admin-events.description-cell.scim-groups-bulk-update-single',
        {
          ...this.defaultEventParams(event),
          groupName: group.name,
        },
      );
    } else if (groups.length === 2) {
      let groupNames = groups.map((group) => group.name).join(', ');
      return this.intl.t('table-cells.admin-events.description-cell.scim-groups-bulk-update', {
        ...this.defaultEventParams(event),
        groupNames,
        totalChanged,
      });
    } else {
      let firstTwoGroups = groups
        .slice(0, 2)
        .map((group) => group.name)
        .join(', ');
      let remainingCount = totalChanged - 2;
      return this.intl.t('table-cells.admin-events.description-cell.scim-groups-bulk-update-many', {
        ...this.defaultEventParams(event),
        firstTwoGroups,
        totalChanged,
        remainingCount,
      });
    }
  }

  formatScimAdminBulkRoleAssignmentEvent(event) {
    let admins = event.value?.admins || [];
    let totalChanged = event.value?.total_admins_changed || admins.length;

    if (admins.length === 0) {
      return this.intl.t(
        'table-cells.admin-events.description-cell.scim-admin-bulk-role-assignment-empty',
        {
          ...this.defaultEventParams(event),
        },
      );
    }

    if (admins.length === 1) {
      let admin = admins[0];
      let fromRole =
        admin.role_change?.from ||
        this.intl.t('table-cells.admin-events.scim-admin-bulk-role-assignment-details.no-role');
      let toRole =
        admin.role_change?.to ||
        this.intl.t('table-cells.admin-events.scim-admin-bulk-role-assignment-details.no-role');

      return this.intl.t(
        'table-cells.admin-events.description-cell.scim-admin-bulk-role-assignment-single',
        {
          ...this.defaultEventParams(event),
          adminName: admin.name,
          fromRole,
          toRole,
        },
      );
    } else if (admins.length === 2) {
      let adminNames = admins.map((admin) => admin.name).join(', ');
      return this.intl.t(
        'table-cells.admin-events.description-cell.scim-admin-bulk-role-assignment',
        {
          ...this.defaultEventParams(event),
          totalChanged,
          adminNames,
        },
      );
    } else {
      let firstTwoAdmins = admins
        .slice(0, 2)
        .map((admin) => admin.name)
        .join(', ');
      let remainingCount = totalChanged - 2;

      return this.intl.t(
        'table-cells.admin-events.description-cell.scim-admin-bulk-role-assignment-many',
        {
          ...this.defaultEventParams(event),
          totalChanged,
          firstTwoAdmins,
          remainingCount,
        },
      );
    }
  }
}
